import React, { useState, useEffect } from 'react';
import './FilterModal.css';
import axiosInstance from '../../Config/axiosConfig';

const FilterModal = ({ 
  visible, 
  onClose, 
  categories, 
  onFilterSelect,
  selectedEducationLevelId 
}) => {
  const [subjects, setSubjects] = useState([]);
  const [educationClasses, setEducationClasses] = useState([]);
  const [educationLevels, setEducationLevels] = useState([]);
  const [priceRange, setPriceRange] = useState(5000);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(null);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const response = await axiosInstance.post("Public/get-lookups");
        if (response.data.code === 200) {
          setSubjects(response.data.data.subjects || []);
          setEducationClasses(response.data.data.educationClasses || []);
          setEducationLevels(response.data.data.educationLevels || []);
        }
      } catch (err) {
        console.error(err);
      }
    };
    if (visible) {
      fetchLookups();
    }
  }, [visible]);

  useEffect(() => {
    // Check if there are any active filters
    const active = selectedSubject || selectedClass || selectedLevel || priceRange < 10000;
    setHasActiveFilters(active);
  }, [selectedSubject, selectedClass, selectedLevel, priceRange]);

  const handleApply = () => {
    const filters = {};

    if (selectedSubject && selectedSubject !== 0) {
      filters.subjectId = selectedSubject;
    }

    if (selectedClass && selectedClass !== 0) {
      filters.educationClass = selectedClass;
    }

    if (selectedLevel && selectedLevel !== 0) {
      filters.educationLevelId = selectedLevel;
    }

    if (priceRange && priceRange < 10000) {
      filters.maxPrice = priceRange;
    }

    console.log("Applying filters:", filters);
    
    // Pass all filters to parent
    onFilterSelect(filters);
    onClose();
  };

  const openDropdown = (type) => {
    setDropdownVisible(type);
  };

  const closeDropdown = () => {
    setDropdownVisible(null);
  };

  const handleSelect = (type, value) => {
    switch (type) {
      case "subject":
        setSelectedSubject(value);
        break;
      case "class":
        setSelectedClass(value);
        break;
      case "level":
        setSelectedLevel(value);
        break;
    }
    closeDropdown();
  };

  const getSelectedLabel = (type) => {
    switch (type) {
      case "subject":
        return selectedSubject
          ? subjects.find((s) => s.id === selectedSubject)?.name
          : "Select Subject";
      case "class":
        return selectedClass
          ? educationClasses.find((c) => c.id === selectedClass)?.name
          : "Select Class";
      case "level":
        return selectedLevel
          ? educationLevels.find((l) => l.id === selectedLevel)?.name
          : "Select Level";
      default:
        return "Select";
    }
  };

  const clearFilters = () => {
    setSelectedSubject(null);
    setSelectedClass(null);
    setSelectedLevel(null);
    setPriceRange(5000);
  };

  // Render dropdown menu
  const DropdownMenu = () => {
    if (!dropdownVisible) return null;

    const options =
      dropdownVisible === "subject"
        ? subjects
        : dropdownVisible === "class"
        ? educationClasses
        : educationLevels;

    const dropdownTitle =
      dropdownVisible === "subject"
        ? "Select Subject"
        : dropdownVisible === "class"
        ? "Select Class"
        : "Select Level";

    return (
      <div className="dropdown-overlay" onClick={closeDropdown}>
        <div className="dropdown-container" onClick={(e) => e.stopPropagation()}>
          <div className="dropdown-header">
            <h3 className="dropdown-title">{dropdownTitle}</h3>
            <button className="dropdown-close-button" onClick={closeDropdown}>
              ×
            </button>
          </div>
          <div className="dropdown-list">
            {options.map((item) => (
              <div
                key={item.id}
                className={`dropdown-item ${
                  (dropdownVisible === "subject" && selectedSubject === item.id) ||
                  (dropdownVisible === "class" && selectedClass === item.id) ||
                  (dropdownVisible === "level" && selectedLevel === item.id)
                    ? 'selected'
                    : ''
                }`}
                onClick={() => handleSelect(dropdownVisible, item.id)}
              >
                <span className="dropdown-item-text">{item.name}</span>
                {(dropdownVisible === "subject" && selectedSubject === item.id) ||
                (dropdownVisible === "class" && selectedClass === item.id) ||
                (dropdownVisible === "level" && selectedLevel === item.id) ? (
                  <span className="dropdown-checkmark">✓</span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Dropdown button component
  const DropdownButton = ({ type }) => (
    <div 
      className={`dropdown-button ${!getSelectedLabel(type).startsWith("Select") ? 'selected' : ''}`}
      onClick={() => openDropdown(type)}
    >
      <span className="dropdown-button-text">{getSelectedLabel(type)}</span>
      <span className="dropdown-arrow">▼</span>
    </div>
  );

  if (!visible) return null;

  return (
    <>
      <div className="filter-modal-overlay" onClick={onClose}>
        <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
          <div className="filter-modal-header">
            <h2>Filter Classes</h2>
            <div className="header-actions">
              {hasActiveFilters && (
                <button className="clear-filters-button" onClick={clearFilters}>
                  <span className="clear-icon">🗑️</span>
                  Clear
                </button>
              )}
              <button className="close-button" onClick={onClose}>×</button>
            </div>
          </div>
          
          <div className="filter-modal-content">
            {/* Education Level */}
            <div className="filter-section">
              <label className="section-label">Education Level</label>
              <DropdownButton type="level" />
            </div>

            {/* Education Class */}
            <div className="filter-section">
              <label className="section-label">Education Class</label>
              <DropdownButton type="class" />
            </div>

            {/* Subject */}
            <div className="filter-section">
              <label className="section-label">Subject</label>
              <DropdownButton type="subject" />
            </div>

            {/* Price Range */}
            <div className="filter-section">
              <div className="section-header">
                <label className="section-label">Maximum Price</label>
                <span className="price-value">Tsh {priceRange.toLocaleString()}</span>
              </div>
              <div className="slider-container">
                <input
                  type="range"
                  min="0"
                  max="10000"
                  step="500"
                  value={priceRange}
                  onChange={(e) => setPriceRange(Number(e.target.value))}
                  className="price-slider"
                />
                <div className="slider-labels">
                  <span className="slider-label">0</span>
                  <span className="slider-label">10,000</span>
                </div>
              </div>
            </div>
          </div>

          {/* Apply Button */}
          <div className="filter-footer">
            <button className="apply-button" onClick={handleApply}>
              Apply Filters
              <span className="apply-arrow">→</span>
            </button>
          </div>
        </div>
      </div>

      {/* Dropdown Menu */}
      <DropdownMenu />
    </>
  );
};

export default FilterModal;